<template>
  <div class="main">
    <Header
      navbar-type="goBack"
      @clicked="goBack"
      :screen-title="t('PAYJOY_CARD_VIEWS.CC_CALCULATOR_TITLE')"
    />
    <TransactionCalculator
      :estimateData="withdrawalEstimateRes"
      :maxAmount="maxAmount"
      @onEstimateAmountChange="handleEstimateAmountChange"
    />
  </div>
  <Alert
    v-if="showAlert"
    class="mx-3"
    :message="t('CL_ERROR_CODES.862')"
    alertIcon="fa fa-info-circle"
    alertType="alert-error"
    @onClose="() => (showAlert = false)"
  />
  <LoadingOverlay :loading="loading" />
</template>

<script setup lang="ts">
import { ref, onMounted, getCurrentInstance } from "vue";
import { useRouter, useRoute } from "vue-router";
import Header from "@/shared-components/consumer/Header.vue";
import Alert from "@/shared-components/common/Alert.vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import TransactionCalculator from "@/apps/credit-line/components/CreditCard/TransactionCalculator.vue";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { IWithdrawalEstimate } from "@/apps/credit-line/models/IWithdrawalEstimate";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import { recorder, RECORDING_APPS } from "@/shared-services/recording.service";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { getRandomNumberBetweenRange } from "@/apps/credit-line/helpers/misc.helper";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";

declare const Android: AndroidInterface;

setLanguage(getCurrentInstance()?.proxy);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const loading = ref(false);
const showAlert = ref(false);
const withdrawalEstimateRes = ref<IWithdrawalEstimate | null>(null);
const CreditLineService = new Service();
const navOrigin = route.query.origin as string;
const app = getCurrentInstance()?.appContext.app;
const creditLineId = ref<string>();
const userId = ref<string>();
const maxAmount = ref<number>(0);

const { summary, fetchSummary } = useCreditLineSummary();

const goBack = () => {
  if (route.query.fromHelp) {
    router.back();
  } else {
    router.push({ name: "CreditCardView" });
  }
};

const handleEstimateAmountChange = async (amount: number) => {
  const logData = {
    submittedAmount: amount,
    maxAmount: maxAmount.value,
    userId: userId.value,
    creditLineId: creditLineId.value,
    legacyEventName: "cc_calculator_submit",
    legacyLogKey: "cl_education_tool_user_event",
  };
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.CcClickedCostCalculator,
    "CC_Calculator",
    `amount: ${amount}`,
    logData
  );
  loading.value = true;
  showAlert.value = false;
  withdrawalEstimateRes.value = null;

  try {
    const res = await CreditLineService.getWithdrawalEstimate({
      deviceTag: AuthenticationHelper.getDeviceTag(),
      amount,
    });
    withdrawalEstimateRes.value = res.data;
  } catch (err) {
    withdrawalEstimateRes.value = null;
    if (err.response?.data?.errorCode) {
      ErrorHandler.logCreditlineErrorResponse(err.response.data);
    } else {
      ErrorHandler.handleNetworkErrorAndLog;
    }
    DatadogLogger.Log(
      "CC Payment Calculator error",
      {
        deviceTag: AuthenticationHelper.getDeviceTag(),
        error: err,
      },
      LogType.ERROR
    );
    showAlert.value = true;
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  recorder(app)?.startRecording(RECORDING_APPS.CREDIT_LINE);
  recorder(app)?.updateRecordingInfo(getRandomNumberBetweenRange(1000000, 1), {
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });

  const logData = {
    origin: navOrigin,
    userId,
    creditLineId,
    legacyEventName: "cc_calculator_open",
    legacyLogKey: "cl_education_tool_user_event",
  };

  logAnalyticsEvent(
    AnalyticEvents.CreditLine.CcCalculatorScreenView,
    "CC_Calculator",
    "",
    logData
  );

  if (isAndroid()) {
    Android.finishedLoading();
  }

  await fetchSummary();

  creditLineId.value = summary.value.creditLineId as string;
  userId.value = summary.value.userId as string;
  maxAmount.value = Number(summary.value.availableCredit);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
</style>
